import {tva} from "@gluestack-ui/nativewind-utils/tva";
import {Box, useEventListener} from "@hosttools/frontend";
import type React from "react";
import {memo, useCallback, useRef} from "react";

export type Props = {
    children: React.ReactNode;
    className?: string;
};

const style = tva({
    base: "overflow-y-hidden select-none"
});

const ScrollableParent: React.FC<Props> = ({children, className}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mouseDownRef = useRef(false);
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);

    const stopDragging = useCallback(() => {
        mouseDownRef.current = false;
    }, []);

    useEventListener(
        "mousemove",
        e => {
            e.preventDefault();
            if (!mouseDownRef.current || !containerRef.current) {
                return;
            }
            const x = e.pageX - containerRef.current.offsetLeft;
            const scroll = (x - startXRef.current) * 2;
            containerRef.current.scrollLeft = scrollLeftRef.current - scroll;
        },
        containerRef
    );
    useEventListener(
        "mousedown",
        e => {
            if (!containerRef.current) {
                return;
            }
            mouseDownRef.current = true;
            startXRef.current = e.pageX - containerRef.current.offsetLeft;
            scrollLeftRef.current = containerRef.current.scrollLeft;
        },
        containerRef
    );
    useEventListener("mouseup", stopDragging, containerRef);
    useEventListener("mouseleave", stopDragging, containerRef);

    return (
        <Box className={style({className})} testID="scrollable-parent">
            <Box
                ref={containerRef}
                className="overflow-x-auto border-b border-solid border-blueGray-200"
            >
                {children}
            </Box>
        </Box>
    );
};

export default memo(ScrollableParent);
